// https://docs.getstrada.com/sdks/web/api-reference

import { useLazyQuery } from '@apollo/react-hooks';
import { LicenseStatusType } from 'Admin/components';
import { useAuth0 } from 'Auth';
import GET_CURRENT_USER_LICENSES_QUERY from './getCurrentUserLicensesQuery.graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { VALID_US_STATES_TYPE } from 'Shared/constants/states';
import { getPageFromLocation } from 'Shared/utils';

type CurrentUserLicenses = {
  currentUser: {
    id: string;
    licenses: Array<{
      state: VALID_US_STATES_TYPE;
      status: LicenseStatusType;
    }>;
  };
};

export { GET_CURRENT_USER_LICENSES_QUERY };

export const useStradaControls = () => {
  const isMountedRef = useRef<boolean>(true);

  const location = useLocation();
  const { user, getToken } = useAuth0();

  const stradaChat = window.stradaChat;
  const stradaStartedRef = useRef<boolean>(false);

  const [isOpenChat, setIsOpenChat] = useState<boolean>(false);
  const [isOpeningChatInProgress, setIsOpeningChatInProgress] = useState<
    boolean
  >(false);

  // TODO: Handle error
  const [
    fetchLicenses,
    { data: userLicenses, loading: isFetchingLicenses, called: licensesCalled },
  ] = useLazyQuery<CurrentUserLicenses>(GET_CURRENT_USER_LICENSES_QUERY);

  // Strada chat must be started once before any other action can be performed
  const startStrada = useCallback(async () => {
    if (!stradaChat) {
      return;
    }

    try {
      const token = await getToken();
      const page = getPageFromLocation(location);
      const licenses = (userLicenses?.currentUser.licenses ?? [])
        .filter(license => license.status === 'valid_license')
        .map(license => license.state)
        .join(', ');

      const secureMetadata = token ? { authorization: token } : {};

      const agentVariables = {
        first_name: user?.firstName,
        last_name: user?.lastName,
        email: user?.email,
        agency: user?.partner?.name,
        chatter_type: 'agent',
        page_when_chat_opened: page,
        licenses,
      };

      // Use window.stradaSettings as base settings (set in src/index.tsx)
      const initialSettings = window.stradaSettings;

      stradaStartedRef.current = true; // Mark Strada as started before starting the chat to avoid re-starting the chat

      // Start the chat with initial settings plus the auth token and agent variables
      await stradaChat.start({
        ...initialSettings,
        agentVariables,
        secureMetadata,
      });
    } catch (error) {
      console.error('Failed to start Strada chat:', error);

      stradaStartedRef.current = false; // Reset flag on error so it can be retried

      if (isMountedRef.current) {
        setIsOpeningChatInProgress(false);
      }
    }
  }, [stradaChat, userLicenses, getToken, location, user]);

  // Open the chat with the page where the chat was opened
  const openChat = useCallback(async () => {
    if (!stradaChat) {
      return;
    }

    try {
      await stradaChat.open();

      if (isMountedRef.current) {
        setIsOpenChat(true);
        setIsOpeningChatInProgress(false);
      }
    } catch (error) {
      console.error('Failed to open Strada chat:', error);

      if (isMountedRef.current) {
        setIsOpeningChatInProgress(false);
      }
    }
  }, [stradaChat]);

  const closeChat = useCallback(async () => {
    if (!stradaChat) {
      return;
    }

    try {
      await stradaChat.close();

      if (isMountedRef.current) {
        setIsOpenChat(false);
      }
    } catch (error) {
      console.error('Failed to close Strada chat:', error);

      if (isMountedRef.current) {
        setIsOpenChat(false);
      }
    }
  }, [stradaChat]);

  // TODO: Integrate NATIVE STRADA WIDGET ACTION 'Start new chat' (End Chat)
  // We should listen for the 'End Chat' event from the chat-widget to close the chat and start a new one at open,
  // sending the updated agent variables to the new chat

  // NATIVE STRADA WIDGET ACTION: 'Minimize'
  // This effect listens to DOM to detect when the chat is manually minimized and sets the chat state to closed
  useEffect(() => {
    if (!isOpenChat) {
      return;
    }

    let observer: MutationObserver | null = null;

    // Wait a bit for the DOM to render after opening
    const timeoutId = setTimeout(() => {
      const chatContainer = document.getElementById('strada-chat-container');

      if (!chatContainer) {
        console.warn('Strada chat container not found in DOM');
        return;
      }

      // Watch for style changes on the container
      observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'style'
          ) {
            const target = mutation.target as HTMLElement;
            const isVisible = target.style.display !== 'none';

            // If chat was closed/minimized (display changed to none)
            if (!isVisible && isMountedRef.current && isOpenChat) {
              setIsOpenChat(false);
            }
          }
        });
      });

      observer.observe(chatContainer, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [isOpenChat]);

  // Clean up any pending operations on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (window.stradaChat) {
        window.stradaChat.close().catch(error => {
          console.error('Failed to close Strada chat on unmount:', error);
        });
      }
    };
  }, []); // should use stradaChat dependency?

  // "Open Chat" effect:
  // Handle opening chat in 2 different ways:
  // - First time => fetch licenses (if not fetched) → wait for loading → start Strada → open chat
  // - Subsequent times => just open chat
  useEffect(() => {
    const handleOpen = async () => {
      // Stop if a opening chat is in progress
      if (!isMountedRef.current || !isOpeningChatInProgress) {
        return;
      }

      // If already started, just open (Subsequent times opening)
      if (stradaStartedRef.current) {
        await openChat();
        return;
      }

      /* First time opening: */
      // 1. Fetch licenses before starting Strada (if not already called)
      if (!licensesCalled) {
        fetchLicenses();
        return; // Wait for licenses to be fetched (this will re-trigger this effect)
      }
      // 2. Wait while licenses are still loading
      if (isFetchingLicenses) {
        return; // Wait for licenses to finish loading (success or error)
      }
      // 3. Start Strada and open chat (licenses are done loading, use whatever we got)
      try {
        await startStrada();
        await openChat();
      } catch (error) {
        // Error already handled and state cleaned up by startStrada/openChat
        // This catch prevents unhandled promise rejections
      }
    };

    handleOpen();
  }, [
    isOpeningChatInProgress,
    licensesCalled,
    isFetchingLicenses,
    fetchLicenses,
    openChat,
    startStrada,
  ]);

  const toggleOpen = () => {
    if (!stradaChat || isOpeningChatInProgress) {
      return;
    }

    if (!isOpenChat) {
      setIsOpeningChatInProgress(true); // Trigger the "Open Chat" effect above
    } else {
      closeChat();
    }
  };

  return { loaded: !!stradaChat, toggleOpen, isOpen: isOpenChat };
};
